import {Injectable, inject} from '@angular/core';
import {Right, TeamRole} from "../page/interfaces";
import {authConfig} from "../page/project/authorization.config";
import {ProjectService} from "./project.service";
import {ProjectStatusPipe} from "../pipes/project-status.pipe";
import {TeamRolePipe} from "../pipes/team-role.pipe";

interface User {
    id: string;
    name: string;
    email: string;
    phone?: string;
    rights?: string[];
}

@Injectable({
    providedIn: 'root'
})
export class AccessService {

    private projectService = inject(ProjectService);
    private projectStatusPipe = inject(ProjectStatusPipe);
    private teamRolePipe = inject(TeamRolePipe);

    private _user: User = null;

    get user(): User {
        return this._user;
    }

    set user(user: any) {
        this._user = user;
    }

    /* Checks whether the user has at least one of the given rights */
    hasRights(rights: Right | Right[]): boolean {
        if (!this._user?.rights) return false;

        const rightsList = [].concat(rights);

        return rightsList.some(right => {
            return this._user.rights.includes(right);
        })
    }

    /* Checks whether the user has a role in a project (except employee) */
    hasRoles(project, roles: TeamRole | TeamRole[]): boolean {
        if (!project) return false;

        const rolesList = [].concat(roles);

        return project.team.some(member => {
            return member.id_member == this._user.id && rolesList.includes(member.role);
        });
    }

    /** Check authorization against a given config */
    authorized(project: any, ID: string, debug = false): boolean {
        const config = authConfig.find(conf => conf.ID == ID);

        if (!config) {
            console.warn("Auth config with ID '" + ID + "' not found!");
            return false;
        }

        return config.groups.some((group, index) => {
            const creatorCheck = !group.creator ? true : project?.id_creator == this._user?.id;
            const statusCheck = !group.status || group.status.length == 0 ? true : this.projectService.hasStatus(project, group.status);
            const notStatusCheck = !group.notStatus || group.notStatus.length == 0 ? true : !this.projectService.hasStatus(project, group.notStatus);
            const rightsCheck = !group.rights || group.rights.length == 0 ? true : this.hasRights(group.rights);
            const rolesCheck = !group.roles || group.roles.length == 0 ? true : this.hasRoles(project, group.roles);
            if (debug) console.log(ID, 'group: ' + (index + 1), 'status: ' + statusCheck, 'not status: ' + notStatusCheck, 'rights: ' + rightsCheck, 'roles: ' + rolesCheck);

            return creatorCheck && statusCheck && notStatusCheck && rightsCheck && rolesCheck;
        });
    }

    /* Return authorization as human-readable string for debugging */
    authReason(project: any, ID: string): string {
        const config = authConfig.find(conf => conf.ID == ID);

        if (!config) {
            console.warn("Auth config with ID '" + ID + "' not found!");
            return '';
        }

        const found = config.groups.find(group => {
            const creatorCheck = !group.creator ? true : project?.id_creator == this._user.id;
            const statusCheck = !group.status || group.status.length == 0 ? true : this.projectService.hasStatus(project, group.status);
            const notStatusCheck = !group.notStatus || group.notStatus.length == 0 ? true : !this.projectService.hasStatus(project, group.notStatus);
            const rightsCheck = !group.rights || group.rights.length == 0 ? true : this.hasRights(group.rights);
            const rolesCheck = !group.roles || group.roles.length == 0 ? true : this.hasRoles(project, group.roles);

            return creatorCheck && statusCheck && notStatusCheck && rightsCheck && rolesCheck;
        });

        if (found) {
            const groups = []

            // only show other groups to PMO
            if (this.hasRights(Right.PMO)) {
                const otherGroups = config.groups.filter(group => group != found);
                otherGroups.forEach(group => {
                    groups.push(this.groupToString(group));
                });
            }

            return "AKTIV<br/>" + this.groupToString(found) + (groups.length > 0 ? ("<br/><br/>ANDERE<br/>" + groups.join("<br/>")) : "");
        } else {
            return '';
        }
    }

    private groupToString(group) {
        const parts = [];
        if (group.creator) {
            parts.push('Ersteller');
        }
        if (group.status) {
            const status = group.status.map(element => this.projectStatusPipe.transform(element)).join(" oder ");
            parts.push('Status: ' + status);
        }
        if (group.notStatus) {
            const status = group.notStatus.map(element => this.projectStatusPipe.transform(element)).join(" oder ");
            parts.push('Nicht Status: ' + status);
        }
        if (group.rights) {
            const rightsMapping = {
                EDIT_MASTER_DATA: "Stammdaten bearbeiten",
                PMO: "PMO (alles immer schreiben und lesen)",
                CEO: "Geschäftsführer (alles immer lesen)",
                MIGRATE_PROJECT: 'Projekt migrieren',
                FINANCE: "Finanz",
                IT: "IT",
                DELETE_PROJECT: "Projekt löschen",
            }

            const rights = group.rights.map(element => rightsMapping[element]).join(" oder ");
            parts.push('Rechte: ' + rights);
        }
        if (group.roles) {
            const roles = group.roles.map(element => this.teamRolePipe.transform(element)).join(" oder ");
            parts.push('Rollen: ' + roles);
        }
        return parts.join(", ");
    }
}
